import { ApiError } from '../../lib/api/error.js';

/**
 * Classifies a failed originals play so the UI can react beyond a flat message:
 * `session-expired` surfaces a relogin CTA, everything else is a plain note.
 */
export type PlayErrorKind = 'insufficient' | 'invalid-selection' | 'session-expired' | 'generic';

export interface MappedPlayError {
  kind: PlayErrorKind;
  /** i18n key — in the `originals` namespace, or the bare error code in `errors`. */
  key: string;
  /** Namespace the key resolves against. */
  ns: 'originals' | 'errors';
}

const INSUFFICIENT_CODE = 'ORIG-004';
const INVALID_SELECTION_CODE = 'ORIG-008';
/** Whole platform paused by the operator — nobody can bet right now. */
const BETTING_STOPPED_CODE = 'GAME-015';
/** This player is over their own ceiling — everyone else can still bet. */
const BET_LIMIT_CODE = 'GAME-016';
/** The bonus this player is working off does not allow this game (#434). */
const PROMO_RESTRICTED_CODE = 'GAME-017';
/** The bet-limit check could not be answered, so the stake was not taken — try again (ADR-0015). */
export const LIMIT_UNCHECKED_CODE = 'GAME-018';
/**
 * The round is drawn and its money is still being confirmed with the wallet
 * (ADR-0014). Not a failure: it settles by itself, and betting again would be
 * a second bet, not a retry of this one.
 */
export const CONFIRMING_CODE = 'ORIG-020';
/** The wallet refused the stake for a reason other than the balance; nothing was taken. */
const REFUSED_CODE = 'ORIG-021';
/**
 * The game seed kept changing while the bet was drawn, so nothing was drawn or
 * taken. Final for this bet — the answer is recorded under its key — so it is
 * shown, not resent; betting again is a new bet.
 */
export const SEED_CHANGED_CODE = 'ORIG-023';

/**
 * Maps any thrown play error to a user-facing message + kind. A 401 here means
 * apiFetch's single-flight refresh already failed and the session is gone — we
 * never want the cryptic "could not load game" (that implies a load failure) or
 * an uncaught rejection. The generic case is "bet could not be placed".
 *
 * The refusal codes carry their own copy rather than falling through to the
 * generic note: "the whole site is paused", "you are over your own limit",
 * "your bonus does not allow this game" and "your limit could not be checked,
 * nothing was taken" are different facts, and a player shown the same sentence
 * for all of them cannot tell whether waiting, retrying, contacting support, or
 * finishing their turnover is the thing to do.
 */
export function mapPlayError(err: unknown): MappedPlayError {
  if (err instanceof ApiError) {
    if (err.code === INSUFFICIENT_CODE) return { kind: 'insufficient', key: 'insufficient', ns: 'originals' };
    if (err.code === INVALID_SELECTION_CODE) return { kind: 'invalid-selection', key: 'ORIG-008', ns: 'errors' };
    if (err.code === BETTING_STOPPED_CODE) return { kind: 'generic', key: BETTING_STOPPED_CODE, ns: 'errors' };
    if (err.code === BET_LIMIT_CODE) return { kind: 'generic', key: BET_LIMIT_CODE, ns: 'errors' };
    if (err.code === LIMIT_UNCHECKED_CODE) {
      return { kind: 'generic', key: LIMIT_UNCHECKED_CODE, ns: 'errors' };
    }
    if (err.code === CONFIRMING_CODE) return { kind: 'generic', key: CONFIRMING_CODE, ns: 'errors' };
    if (err.code === REFUSED_CODE) return { kind: 'generic', key: REFUSED_CODE, ns: 'errors' };
    if (err.code === SEED_CHANGED_CODE) return { kind: 'generic', key: SEED_CHANGED_CODE, ns: 'errors' };
    if (err.code === PROMO_RESTRICTED_CODE) {
      return { kind: 'generic', key: PROMO_RESTRICTED_CODE, ns: 'errors' };
    }
    if (err.status === 401) return { kind: 'session-expired', key: 'sessionExpired', ns: 'originals' };
  }
  return { kind: 'generic', key: 'playFailed', ns: 'originals' };
}
